import React, { useState, useEffect, useRef } from 'react';
import { Input, Tabs, Button, Modal, Form, Select, Menu as AntMenu, Dropdown, Grid, Table } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, MoreOutlined, InfoCircleOutlined, CloseOutlined, EllipsisOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { productsService } from "../../services/ProductsService";
import Menu from '../../components/Menu/Menu';
import './Lists.scss';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MobileBottomMenu from '../../components/MobileBottomMenu/MobileBottomMenu';
import MobileMenu from "../../components/Menu/MobileMenu";
import Header from '../../components/Header/Header';
import { listService } from "../../services/ListService";
import { useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";
import { BrowserMultiFormatReader } from '@zxing/browser';
import BarcodeScannerOverlay from '../../components/BarcodeScannerOverlay/BarcodeScannerOverlay';
import ListStatusGuard from './ListStatusGuard';
import StatusTag from '../../components/StatusTag/StatusTag';

const { Search } = Input;
const { TabPane } = Tabs;
const { Option } = Select;
const { InputRef } = Input;

const MEASUREMENT_LABELS = {
    BUC: 'bucăți',
    KG: 'kilograme',
    baxuri: 'baxuri',
};

const normalizeMeasurementValue = (value) => {
    if (!value) {
        return undefined;
    }

    const normalizedValue = String(value).trim().toLowerCase();

    if (normalizedValue === 'buc' || normalizedValue === 'bucăți' || normalizedValue === 'bucati') {
        return 'BUC';
    }

    if (normalizedValue === 'kg' || normalizedValue === 'kilograme') {
        return 'KG';
    }

    if (normalizedValue === 'baxuri') {
        return 'baxuri';
    }

    return undefined;
};

const formatMeasurementLabel = (value) => {
    const normalizedValue = normalizeMeasurementValue(value);

    return normalizedValue ? MEASUREMENT_LABELS[normalizedValue] : value || '';
};

const getProductsSnapshot = (products) => JSON.stringify(
    (products || [])
        .map(product => ({
            product_id: product.product_id,
            quantity: Number(product.quantity),
            unit_measurement: product.unit_measurement || '',
            instructions: product.instructions || ''
        }))
        .sort((leftProduct, rightProduct) => {
            if (leftProduct.product_id !== rightProduct.product_id) {
                return leftProduct.product_id - rightProduct.product_id;
            }

            return leftProduct.unit_measurement.localeCompare(rightProduct.unit_measurement);
        })
);

const CreateList = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [barcodeFound, setBarcodeFound] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [scannedBarcode, setScannedBarcode] = useState(null);
    const codeReaderRef = useRef(null);

    const location = useLocation();
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [form] = Form.useForm();
    const quantityInputRef = useRef(null);
    const latestSearchRequestRef = useRef(0);

    // Watch form values to enable/disable submit button
    const quantity = Form.useWatch('quantity', form);
    const unitMeasurement = Form.useWatch('unit_measurement', form);
    const selectedProductMeasurement = normalizeMeasurementValue(selectedProduct?.measurement_unit) || selectedProduct?.measurement_unit;
    const resolvedUnitMeasurement = unitMeasurement || selectedProductMeasurement;

    const [listExists, setListExists] = useState(false);
    const [listId, setListId] = useState(null);
    const [lists, setLists] = useState([]);
    const [currentList, setCurrentList] = useState(null);
    const [index, setIndex] = useState(-1);
    const [counts, setCounts] = useState({});
    const [savedProductsSnapshot, setSavedProductsSnapshot] = useState('[]');
    const canManageListProducts = !currentList || ListStatusGuard.canManageProducts(currentList.status);
    const currentProductsSnapshot = getProductsSnapshot(products);
    const hasListChanges = currentProductsSnapshot !== savedProductsSnapshot;
    const canSaveList = Boolean(currentList?.id) &&
        products.length > 0 &&
        (
            currentList?.status === 'draft' ||
            (canManageListProducts && hasListChanges)
        );

    // Mobile detection using Ant Design Grid
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md; // md breakpoint is 768px

    // Info modal
    const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
    const [infoModalContent, setInfoModalContent] = useState('');

    const pathToKey = {
        '/liste-necesar': 'creeaza',
        '/gata-de-trimis': 'gata',
        '/liste-in-ofertare': 'ofertare',
        '/liste-ofertate': 'ofertate',
        '/procesare-manuala': 'procesareManuala',

        '/liste-arhivate': 'arhivate',
    };

    const selectedMenuKey = pathToKey[location.pathname];

    useEffect(() => {
        listService.getCounts()
            .then(res => {
                if (res.data) {
                    setCounts(res.data.list_counts);
                }
            })
            .catch(err => {
                console.error("Eroare la încărcarea listelor:", err);
            });
    }, []);

    // Show info modal
    const showInfoModal = (instructions) => {
        setInfoModalContent(instructions || 'Nu există informații suplimentare.');
        setIsInfoModalVisible(true);
    };

    const checkCameraAvailability = () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            return {
                available: false,
                error: 'Cameră indisponibilă. Asigură-te că aplicația rulează pe HTTPS sau localhost și că browser-ul suportă accesul la cameră.'
            };
        }
        return { available: true };
    };

    const requestCameraPermission = async () => {
        try {
            // Cerem explicit permisiunea camerei înainte de a începe scanarea
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment' // Camera din spate pe mobile
                }
            });
            // Oprim stream-ul imediat - doar vrem să cerem permisiunea
            stream.getTracks().forEach(track => track.stop());
            return { success: true };
        } catch (error) {
            console.error('Camera permission error:', error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                return {
                    success: false,
                    error: 'Accesul la cameră a fost refuzat. Te rugăm să permți accesul la cameră în setările browser-ului sau ale aplicației.'
                };
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                return {
                    success: false,
                    error: 'Nu s-a găsit nicio cameră disponibilă pe acest dispozitiv.'
                };
            } else {
                return {
                    success: false,
                    error: 'Eroare la accesarea camerei: ' + error.message
                };
            }
        }
    };

    useEffect(() => {
        if (!isBarcodeModalOpen) {
            setCameraError(null);
            return;
        }

        const cameraCheck = checkCameraAvailability();
        if (!cameraCheck.available) {
            setCameraError(cameraCheck.error);
            return;
        }

        // Flag pentru a verifica dacă componenta este încă montată
        let isMounted = true;
        let timeoutId = null;
        let isProcessingBarcode = false;

        // BrowserMultiFormatReader suportă implicit rotirea barcode-urilor
        const codeReader = new BrowserMultiFormatReader();
        codeReaderRef.current = codeReader;

        const videoElement = document.getElementById('barcode-video');
        if (!videoElement) {
            setCameraError('Element video nu a fost găsit.');
            return;
        }

        // Funcție pentru a porni scanarea
        const startScanning = async () => {
            try {
                // Cerem explicit permisiunea camerei
                const permissionResult = await requestCameraPermission();

                if (!isMounted) return;

                if (!permissionResult.success) {
                    setCameraError(permissionResult.error);
                    return;
                }

                // Folosim un timeout mic pentru a ne asigura că DOM-ul este gata
                timeoutId = setTimeout(() => {
                    if (!isMounted) return;

                    setIsScanning(true);
                    console.log('Starting barcode scanner...');

                    codeReader.decodeFromVideoDevice(
                        null,
                        videoElement,
                        (result, error) => {
                            if (!isMounted || isProcessingBarcode) return;

                            if (error) {
                                // Ignorăm erorile normale de scanare
                                if (error.name === 'NotFoundException' ||
                                    error.message?.includes('No MultiFormat Readers') ||
                                    error.message?.includes('not found')) {
                                    return;
                                }

                                console.error('Barcode scan error:', error);
                                if (!isMounted) return;

                                setIsScanning(false);
                                if (error.name === 'NotAllowedError') {
                                    setCameraError('Accesul la cameră a fost refuzat. Te rugăm să permți accesul la cameră în setările browser-ului.');
                                } else if (error.name === 'NotFoundError') {
                                    setCameraError('Nu s-a găsit nicio cameră disponibilă.');
                                } else {
                                    setCameraError('Eroare la accesarea camerei: ' + error.message);
                                }
                                return;
                            }

                            if (result) {
                                isProcessingBarcode = true;

                                const barcode = result.getText();
                                console.log('✅ Scanned barcode successfully:', barcode);

                                // Oprim scanarea imediat
                                if (codeReader && typeof codeReader.reset === 'function') {
                                    codeReader.reset();
                                }

                                if (!isMounted) return;

                                setIsScanning(false);
                                setScannedBarcode(barcode);
                                setBarcodeFound(true);

                                // Așteptăm 1 secundă pentru feedback verde
                                setTimeout(() => {
                                    if (!isMounted) return;

                                    setBarcodeFound(false);
                                    setIsSearching(true);

                                    handleBarcodeSearch(barcode).finally(() => {
                                        if (!isMounted) return;

                                        setIsSearching(false);
                                        setIsBarcodeModalOpen(false);
                                        setCameraError(null);
                                        setScannedBarcode(null);
                                    });
                                }, 1000);
                            }
                        }
                    ).catch((error) => {
                        if (!isMounted) return;

                        console.error('Error starting camera:', error);
                        setIsScanning(false);

                        if (error.name === 'NotAllowedError') {
                            setCameraError('Accesul la cameră a fost refuzat. Te rugăm să permți accesul la cameră în setările browser-ului.');
                        } else if (error.name === 'NotFoundError') {
                            setCameraError('Nu s-a găsit nicio cameră disponibilă.');
                        } else if (!error.message?.includes('No MultiFormat Readers')) {
                            setCameraError('Eroare la accesarea camerei: ' + error.message);
                        }
                    });
                }, 100);
            } catch (error) {
                if (!isMounted) return;
                console.error('Error in startScanning:', error);
                setCameraError('Eroare la pornirea camerei: ' + error.message);
            }
        };

        startScanning();

        // Cleanup function - acum la nivelul corect
        return () => {
            isMounted = false;

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            if (codeReaderRef.current && typeof codeReaderRef.current.reset === 'function') {
                try {
                    codeReaderRef.current.reset();
                } catch (error) {
                    console.warn('Error resetting code reader:', error);
                }
            }
        };
    }, [isBarcodeModalOpen]);
    const handleBarcodeSearch = (barcode) => {
        setSearchQuery(barcode);

        return productsService.searchProduct(barcode, 1)
            .then(res => {
                const products = res.data?.products || [];

                if (products.length === 1) {
                    openModal(products[0]);
                } else {
                    setSearchResults(products);
                }
            })
            .catch(err => {
                console.error('Barcode search error:', err);
                toast.error('Produsul nu a fost găsit.');
            });
    };
    useEffect(() => {


        if (id) {
            setLoading(true);
            listService.getSpecificList(id)
                .then(res => {
                    const data = res.data;

                    const loadedProducts = data.necessity_list_items.map(item => ({
                        key: item.id,
                        name: item.product?.name || '',
                        quantity: item.quantity,
                        instructions: item.supplementary_info || '',
                        status: 'Necesar',
                        unit_measurement: item.unit_measurement || '',
                        product_id: item.product_id,
                    }));

                    setProducts(loadedProducts);
                    setSavedProductsSnapshot(getProductsSnapshot(loadedProducts));
                    setListId(data.id);
                    setListExists(true);
                    setCurrentList(data);
                })
                .catch(error => {
                    console.error("Eroare la încărcarea listei:", error);
                    toast.error("Nu s-a putut încărca lista.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [id]);


    const handleSearch = (value) => {
        setSearchQuery(value);
    };

    useEffect(() => {
        const trimmedQuery = searchQuery.trim();

        if (!trimmedQuery) {
            setSearchResults([]);
            setIsSearchLoading(false);
            return;
        }

        const debounceTimeout = setTimeout(() => {
            const requestId = latestSearchRequestRef.current + 1;
            latestSearchRequestRef.current = requestId;
            setIsSearchLoading(true);

            productsService.searchProduct(trimmedQuery, 1)
                .then(res => {
                    if (latestSearchRequestRef.current !== requestId) {
                        return;
                    }

                    setSearchResults(res.data?.products || []);
                })
                .catch(err => {
                    if (latestSearchRequestRef.current !== requestId) {
                        return;
                    }

                    console.error("Eroare la căutare:", err);
                    setSearchResults([]);
                })
                .finally(() => {
                    if (latestSearchRequestRef.current === requestId) {
                        setIsSearchLoading(false);
                    }
                });
        }, 250);

        return () => clearTimeout(debounceTimeout);
    }, [searchQuery]);

    useEffect(() => {
        if (selectedProduct && isModalVisible) {
            form.setFieldsValue({
                quantity: selectedProduct.quantity || '',
                unit_measurement: normalizeMeasurementValue(selectedProduct.measurement_unit),
                supplementary_info: selectedProduct.supplementary_info || '',
            });
        }
    }, [selectedProduct, isModalVisible]);
    const handleEdit = (record) => {
        if (!canManageListProducts) {
            return;
        }

        const normalizedMeasurement = normalizeMeasurementValue(record.unit_measurement);

        setSelectedProduct({
            id: record.product_id,
            name: record.name,
            measurement_unit: normalizedMeasurement || record.unit_measurement,
            quantity: record.quantity,
            supplementary_info: record.instructions
        });
        setIsEditing(true);
        setIsModalVisible(true);
        form.setFieldsValue({
            quantity: record.quantity,
            unit_measurement: normalizedMeasurement || record.unit_measurement,
            supplementary_info: record.instructions || ''
        });
    };
    const handleDeleteProduct = product => {
        if (!canManageListProducts) {
            return;
        }

        listService.deleteProduct(listId, product.product_id)
            .then(() => {
                toast.success('Ștergere reușită');

                setProducts(prev => prev.filter(currentProduct => currentProduct.product_id !== product.product_id));
                setIsDeleteModalVisible(false);
            })
            .catch(() => {
                console.error('Eroare');
                toast.error('A apărut o eroare la ștergere.');
                setIsDeleteModalVisible(false);
            });
    };
    const openModal = (product) => {
        if (!canManageListProducts) {
            return;
        }

        setIsEditing(false);
        setSelectedProduct(product);
        setIsModalVisible(true);
        form.setFieldsValue({
            name: product.name,
            quantity: '',
            unit_measurement: '',
            supplementary_info: ''
        });
    };

    const showDeleteModal = (product) => {
        if (!canManageListProducts) {
            return;
        }

        setSelectedProduct(product);

        setIsDeleteModalVisible(true);
    };

    const handleCancelDelete = () => {
        setIsDeleteModalVisible(false);
    };
    const addProduct = () => {
        if (!canManageListProducts) {
            return;
        }

        form.validateFields().then(values => {
            if (!selectedProduct) {
                console.error("No product selected");
                return;
            }

            const options = {
                product_id: selectedProduct.id,
                quantity: values.quantity,
                unit_measurement: values.unit_measurement,
                supplementary_info: values.supplementary_info || '',
                list_description: values.list_description || '',
            };

            if (listId) {
                options.list_id = listId;
            }

            setLoading(true);

            listService.addProductToList(options)
                .then(response => {
                    const item = response.data.item ?? response.data;

                    if (!item || !item.id) {
                        toast.error("Eroare: Răspuns invalid de la server.");
                        return;
                    }

                    const newProduct = {
                        key: item.id,
                        name: selectedProduct.name,
                        quantity: item.quantity,
                        instructions: item.supplementary_info || '',
                        status: 'Necesar',
                        unit_measurement: item.unit_measurement,
                        product_id: item.product_id,
                    };

                    // Check if product already exists with same product_id and unit_measurement
                    setProducts(prev => {
                        const existingIndex = prev.findIndex(
                            p => p.product_id === item.product_id &&
                                p.unit_measurement === item.unit_measurement
                        );

                        if (existingIndex !== -1) {
                            // Replace existing product with updated one
                            const updated = [...prev];
                            updated[existingIndex] = newProduct;
                            return updated;
                        } else {
                            // Add new product
                            return [...prev, newProduct];
                        }
                    });

                    if (!listId && item.necessity_list_id) {
                        setListId(item.necessity_list_id);
                        setListExists(true);

                        listService.getSpecificList(item.necessity_list_id)
                            .then(res => {
                                setCurrentList(res.data);
                            })
                            .catch(err => {
                                console.error("Eroare la obținerea listei nou create:", err);
                            });
                    }

                    toast.success("Produs adăugat!");
                    closeModalAndReset();
                })
                .catch(err => {
                    console.error('Eroare la salvare:', err);
                    toast.error("A apărut o eroare.");
                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };
    const editProduct = (values) => {
        if (!canManageListProducts) {
            return;
        }

        if (!selectedProduct) {
            console.error("No product selected");
            return;
        }

        if (!listId) {
            console.error("No list ID to edit product");
            return;
        }

        const options = {
            product_id: selectedProduct.id,
            quantity: values.quantity,
            unit_measurement: values.unit_measurement || selectedProductMeasurement,
            supplementary_info: values.supplementary_info || '',
            list_id: listId,
        };

        setLoading(true);

        listService.editProduct(listId, options)
            .then(response => {
                const item = response.data.item ?? response.data;

                if (!item || !item.id) {
                    toast.error("Eroare: Răspuns invalid de la server.");
                    return;
                }

                const updatedProduct = {
                    key: item.id,
                    name: selectedProduct.name,
                    quantity: item.quantity,
                    instructions: item.supplementary_info || '',
                    status: 'Necesar',
                    unit_measurement: item.unit_measurement,
                    product_id: item.product_id,
                };

                setProducts(prev =>
                    prev.map(p => p.product_id === selectedProduct.id ? updatedProduct : p)
                );

                toast.success("Produs actualizat!");
                closeModalAndReset();
            })
            .catch(err => {
                console.error('Eroare la salvare:', err);
                toast.error("A apărut o eroare.");
            })
            .finally(() => {
                setLoading(false);
            });
    };
    const handleSubmitProduct = () => {
        form.validateFields().then(values => {
            if (isEditing) {
                editProduct(values);
            } else {
                addProduct(values);
            }
        });
    };
    const closeModalAndReset = () => {
        setIsModalVisible(false);
        setIsEditing(false);
        setSelectedProduct(null);
        form.resetFields();
        setSearchQuery('');
        setSearchResults([]);
    };

    // Desktop columns
    const desktopColumns = [
        {
            title: 'Nume produs',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Cantitate',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (_, record) => `${record.quantity} ${formatMeasurementLabel(record.unit_measurement)}`.trim(),
        },
        {
            title: 'Indicații',
            dataIndex: 'instructions',
            key: 'instructions',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <StatusTag status={status} />,
        },
        {
            title: 'Editează',
            key: 'edit',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                    disabled={!canManageListProducts}
                />
            ),
        },
        {
            title: 'Șterge',
            key: 'delete',
            render: (_, record) => (
                <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        setSelectedProduct(record);
                        showDeleteModal(record);
                    }}
                    disabled={!canManageListProducts}
                />
            ),
        },
    ];

    // Desktop columns only - mobile uses card layout
    const columns = desktopColumns;

    // Helper function to get menu items for a row
    const getMenuItemsForRow = (record) => [
        {
            key: 'info',
            label: 'Informații suplim...',
            icon: <InfoCircleOutlined />,
            onClick: () => showInfoModal(record.instructions),
        },
        {
            key: 'edit',
            label: 'Editează',
            icon: <EditOutlined />,
            disabled: !canManageListProducts,
            onClick: () => handleEdit(record),
        },
        {
            key: 'delete',
            label: 'Șterge Produs',
            icon: <DeleteOutlined />,
            danger: true,
            disabled: !canManageListProducts,
            onClick: () => {
                setSelectedProduct(record);
                showDeleteModal(record);
            },
        },
    ];

    // Helper function to format quantity
    const formatQuantity = (record) => {
        return `${record.quantity} ${formatMeasurementLabel(record.unit_measurement)}`.trim();
    };

    // Mobile List Component - Table-like layout
    const MobileList = () => {
        if (products.length === 0) {
            return (
                <div className="list__empty-state">
                    <img
                        src="/images/no-offers-icon.png"
                        alt="Niciun produs"
                        className="list__empty-state-image"
                    />
                    <p className="list__empty-state-text">
                        Niciun produs adăugat.
                    </p>
                </div>
            );
        }

        return (
            <div className="list__mobile-table-wrapper">
                {/* Table Header */}
                <div className="list__mobile-table-header">
                    <div className="list__mobile-table-header-cell list__mobile-table-header-name">
                        Nume produs
                    </div>
                    <div className="list__mobile-table-header-cell list__mobile-table-header-quantity">
                        Cantitate
                    </div>
                    <div className="list__mobile-table-header-cell list__mobile-table-header-actions">
                        Opțiuni
                    </div>
                </div>

                {/* Table Rows */}
                <div className="list__mobile-list">
                    {products.map((row) => (
                        <div key={row.key} className="list__mobile-card">
                            <div className="list__mobile-card-cell list__mobile-card-name" title={row.name}>
                                {row.name}
                            </div>
                            <div className="list__mobile-card-cell list__mobile-card-quantity">
                                {formatQuantity(row)}
                            </div>
                            <div className="list__mobile-card-cell list__mobile-card-actions">
                                <Dropdown
                                    menu={{ items: getMenuItemsForRow(row) }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <Button
                                        type="text"
                                        shape="circle"
                                        icon={<EllipsisOutlined />}
                                        className="list__mobile-card-actions-btn"
                                    />
                                </Dropdown>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleSaveList = listData => {
        if (!listData?.id) {
            toast.error('Lista nu este pregătită pentru salvare.');
            return;
        }

        if (listData.status !== 'draft') {
            if (!hasListChanges) {
                return;
            }

            setSavedProductsSnapshot(currentProductsSnapshot);
            toast.success('Modificările listei sunt deja salvate.');
            return;
        }

        listService.saveList(listData)
            .then(() => {
                setSavedProductsSnapshot(currentProductsSnapshot);
                setCurrentList(prevList => (
                    prevList
                        ? { ...prevList, status: 'ready' }
                        : prevList
                ));
                toast.success('Lista a fost salvată.');
            })
            .catch(() => {
                console.error('Eroare');
                toast.error('A apărut o eroare la salvarea listei.');
            });
    };

    return (
        <div className='standard products list'>
            <Header />


            <div className='standard__container list'>
                <Menu />
                <div className='standard__wrapper'>
                    <div className='list__mobile-content'>
                        <h2 className='standard__page-title list__page-title'>{products.length > 0 ? 'Editează lista de necesar' : 'Creează lista de necesar'}</h2>
                        <h4 className='standard__page-info list__page-info'>
                            {products.length > 0 ? 'Modifică produsele, cantitățile sau alte informații din listă înainte de trimitere' : 'Creează o listă importată sau adaugă manual produse în lista de necesar, căutându-le și completând formularul.'}

                        </h4>

                        <div className='list__search-box'>
                            <div className='list__search-mobile'>
                                <Search
                                    className="list__search-input"
                                    placeholder="Caută produs..."
                                    onChange={(e) => handleSearch(e.target.value)}
                                    value={searchQuery}
                                    enterButton
                                    allowClear
                                    disabled={!canManageListProducts}
                                />
                                <Button
                                    className="list__barcode-button"
                                    onClick={() => setIsBarcodeModalOpen(true)}
                                    disabled={!canManageListProducts}
                                >
                                    <img src={`${process.env.PUBLIC_URL || ''}/images/barcode.png`} alt="Scan barcode" />
                                </Button>
                            </div>
                            {searchQuery.trim().length > 0 && (
                                <ul className="list__search-dropdown">
                                    {isSearchLoading ? (
                                        <li className="list__search-status">
                                            Se caută produse...
                                        </li>
                                    ) : searchResults.length > 0 ? (
                                        searchResults.map((product) => {
                                            const regex = new RegExp(`(${searchQuery})`, "gi");
                                            const highlightedName = product.name.replace(regex, (match) => `<span class="search__highlight">${match}</span>`);

                                            return (
                                                <div className='search__result' key={product.id}>
                                                    <img src='/images/search.png' alt='search' />
                                                    <li>
                                                        <a
                                                            className='standard__link'
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                openModal(product);
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: highlightedName }}
                                                        />
                                                    </li>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <li className="list__search-no-results">
                                            Niciun produs găsit
                                        </li>
                                    )}
                                </ul>
                            )}
                        </div>
                        {products.length > 0 ?
                            <Link to='/gata-de-trimis' className='standard__link'>
                                <div className='biding__back'>
                                    <img src="/images/back.png" />
                                    <h4>Înapoi</h4>
                                </div>
                            </Link> : ''
                        }


                        <div className='list__tabs-wrapper'>
                            <AntMenu
                                mode="horizontal"
                                selectedKeys={[selectedMenuKey]}
                                style={{ marginTop: 16 }}
                                onClick={(e) => {
                                    switch (e.key) {
                                        case 'creeaza':
                                            navigate('/liste-necesar');
                                            break;
                                        case 'gata':
                                            navigate('/gata-de-trimis');
                                            break;
                                        case 'ofertare':
                                            navigate('/liste-in-ofertare');
                                            break;
                                        case 'ofertate':
                                            navigate('/liste-ofertate');
                                            break;
                                        case 'procesareManuala':
                                            navigate('/procesare-manuala');
                                            break;
                                        case 'arhivate':
                                            navigate('/liste-arhivate');
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                            >
                                <AntMenu.Item key="creeaza">Creează listă</AntMenu.Item>
                                <AntMenu.Item key="gata">
                                    Gata de trimis ({counts.ready_to_send || 0})
                                </AntMenu.Item>
                                <AntMenu.Item key="ofertare">
                                    Liste în ofertare ({counts.offering || 0})
                                </AntMenu.Item>
                                <AntMenu.Item key="ofertate">
                                    Liste ofertate ({counts.offered || 0})
                                </AntMenu.Item>
                                <AntMenu.Item key="procesareManuala">
                                    Procesare manuala ({counts.manual_processing || 0})
                                </AntMenu.Item>
                                <AntMenu.Item key="arhivate">
                                    Liste arhivate ({counts.archived || 0})
                                </AntMenu.Item>
                            </AntMenu>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>


                            {currentList && currentList.created_at && (
                                <div style={{ textAlign: 'right', marginLeft: 'auto', marginTop: '10px' }}>
                                    <div>Listă Nr. {currentList.id} din  {new Date(currentList.created_at).toLocaleDateString('ro-RO')}</div>
                                </div>
                            )}
                        </div>
                        <div className="list__table-wrapper">

                            <div className='list__table-content'>
                                {loading ? (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
                                ) : !isMobile ? (
                                    <Table
                                        dataSource={products}
                                        columns={columns}
                                        rowKey="key"
                                        pagination={false}
                                        scroll={{ x: true }}
                                        locale={{
                                            emptyText: (
                                                <div className="list__empty-state">
                                                    <img
                                                        src="/images/no-offers-icon.png"
                                                        alt="Niciun produs"
                                                        className="list__empty-state-image"
                                                    />
                                                    <p className="list__empty-state-text">
                                                        Niciun produs adăugat.
                                                    </p>
                                                </div>
                                            )
                                        }}
                                    />
                                ) : (
                                    <MobileList />
                                )}
                            </div>
                            <div className='list__table-footer'>
                                <h4 className='list__table-footer-info'>
                                    După salvare, lista va apărea în meniul Gata de trimis.
                                </h4>
                                <Button
                                    type="primary"
                                    className='list__save-button'
                                    icon={<SaveOutlined />}
                                    onClick={() => handleSaveList(currentList)}
                                    disabled={!canSaveList}
                                >
                                    Salvează lista
                                </Button>
                            </div>
                        </div>


                    </div>
                </div>
            </div>

            <MobileBottomMenu />

            <Modal
                title={`Doriți să adăugați „${selectedProduct?.name}” în lista de necesar?`}
                open={isModalVisible}
                onCancel={closeModalAndReset}
                onOk={handleSubmitProduct}
                okText={isEditing ? 'Editează' : 'Adaugă în listă'}
                cancelText="Anulează"
                okButtonProps={{
                    disabled: !canManageListProducts || !quantity || (!isEditing && !resolvedUnitMeasurement)
                }}
                afterOpenChange={(open) => {
                    if (open && quantityInputRef.current) {
                        // Focus after modal animation completes
                        setTimeout(() => {
                            quantityInputRef.current?.focus();
                        }, 100);
                    }
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="quantity" label="Cantitate dorită" rules={[{ required: true, message: 'Cantitatea este obligatorie' }]}>
                        <Input
                            ref={quantityInputRef}
                            autoFocus={isModalVisible}
                            type="number"
                            placeholder="Introduceți cantitatea"
                            onPressEnter={() => {
                                // Allow Enter to submit if form is valid
                                if (quantity && unitMeasurement) {
                                    handleSubmitProduct();
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="unit_measurement"
                        label="Unitate de măsură"
                        rules={[{ required: !isEditing, message: 'Unitatea de măsură este obligatorie' }]}
                    >
                        <Select placeholder="Selectează">
                            <Option value="BUC">bucăți</Option>
                            <Option value="baxuri">baxuri</Option>
                            <Option value="KG">kilograme</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="supplementary_info" label="Informații suplimentare (opțional)">
                        <Input.TextArea />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Confirmă ștergere"
                open={isDeleteModalVisible}
                onOk={() => handleDeleteProduct(selectedProduct)}
                onCancel={handleCancelDelete}
                okText="Șterge"
                cancelText="Anulează"
            >
                <p>Sunteți sigur că doriți să ștergeți acest produs?</p>
            </Modal>
            <Modal
                open={isBarcodeModalOpen}
                footer={null}
                onCancel={() => {
                    setIsBarcodeModalOpen(false);
                    setCameraError(null);
                    setIsScanning(false);
                    setBarcodeFound(false);
                    setIsSearching(false);
                    setScannedBarcode(null);
                }}
                title="Scanează codul de bare"
                width="100%"
                style={{ top: 0 }}
                styles={{ body: { padding: 0 } }}
            >
                {cameraError ? (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                        <p style={{ color: '#ff4d4f', marginBottom: 16 }}>{cameraError}</p>
                        <p style={{ fontSize: 12, color: '#666' }}>
                            Notă: Funcționalitatea de scanare necesită HTTPS sau localhost pentru a accesa camera.
                        </p>
                    </div>
                ) : (
                    <div style={{ position: 'relative' }}>
                        <video
                            id="barcode-video"
                            style={{ width: '100%', height: '80vh', objectFit: 'cover' }}
                        />
                        <BarcodeScannerOverlay
                            isScanning={isScanning}
                            barcodeFound={barcodeFound}
                            isSearching={isSearching}
                            scannedBarcode={scannedBarcode}
                        />
                    </div>
                )}
            </Modal>

            {/* Mobile Info Modal */}
            <Modal
                open={isInfoModalVisible}
                footer={null}
                onCancel={() => setIsInfoModalVisible(false)}
                title="Informații suplimentare"
                className="list__info-modal"
                centered
            >
                <div className="list__info-modal-content">
                    <p>{infoModalContent}</p>
                    <Button
                        className="list__info-modal-close"
                        onClick={() => setIsInfoModalVisible(false)}
                        icon={<CloseOutlined />}
                    >
                        Închide
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default CreateList;
